import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next"; // Import useTranslation
import cn from "classnames";
import { toast } from "react-toastify";
import style from "../common.module.scss";
import { File } from "../../../../services";
import EyeIcon from "../../../shared/icons/EyeIcon";
import Loading from "../../../shared/Loading/Loading";
import { useClickOutside } from "../../../../hooks/useClickOutside";
import DocumentNewIcon from "../../../shared/icons/DocumentNewIcon";
import PhotoViewer from "../../../partners/components/PhotoViewer";
import moment from "moment";
import UnloadIcon from "../../../shared/icons/UnloadIcon";
import ArrowDownIcon from "../../../shared/icons/ArrowDownIcon";
import DeleteNewIcon from "../../../shared/icons/DeleteNewIcon";
import { useSelector } from "react-redux";
import CustomButton from "../../../custom/custom-button/CustomButton";
import { useScreenSize } from "../../../../hooks/useScreenSize";

const ListFilesOrder = ({ setShowModal, documents, refreshDocuments, orderStatus }) => {
  const { t } = useTranslation();
  const { user } = useSelector((state) => state);
  const { isMobileView } = useScreenSize();
  const [loadingStates, setLoadingStates] = useState({});
  const [loadingStatesDownload, setLoadingStatesDownload] = useState({});
  const [isOpen, setIsOpen] = useState(false);
  const [imgUrl, setImgUrl] = useState();
  const [currentDocId, setCurrentDocId] = useState("");
  const [cachedFiles, setCachedFiles] = useState({});
  const [showAllDocuments, setShowAllDocuments] = useState(false);
  const [docIdToDelete, setDocIdToDelete] = useState(null);
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [loadingRemoving, setLoadingRemovinge] = useState(false);

  const modalRef = useRef(null);

  useClickOutside(modalRef, () => {
    setShowModal(false);
  });

  const handleClose = () => {
    setIsOpen(false);
  };

  const setLoadingViewer = (docId, isLoading) => {
    setLoadingStates((prev) => ({ ...prev, [docId]: isLoading }));
  };

  const setLoadingDownload = (docId, isLoading) => {
    setLoadingStatesDownload((prev) => ({ ...prev, [docId]: isLoading }));
  };

  const getFileExtension = (fileName) => {
    if (!fileName) return "";
    const parts = fileName.split(".");
    return parts.length > 1 ? parts[parts.length - 1].toUpperCase() : "";
  };

  const handleOpenFile = (doc) => {
    if (!doc) {
      toast.error(t('orderInfo.listFilesOrder.fileNotFound'));
      return;
    }

    if (doc.contentType === "application/pdf" || getFileExtension(doc.fileName) === "PDF") {
      if (cachedFiles[doc.fileId]) {
        window.open(cachedFiles[doc.fileId], "_blank");
        return;
      }

      setLoadingViewer(doc.id, true);
      File.downloadFileById(doc.fileId)
        .then((response) => {
          const file = new Blob([response.data], {
            type: response.data.type || "application/pdf",
          });

          const fileURL = URL.createObjectURL(file);
          window.open(fileURL, "_blank");
          setLoadingViewer(doc.id, false);

          setCachedFiles((prev) => ({
            ...prev,
            [doc.fileId]: fileURL,
          }));
        })
        .catch((error) => {
          setLoadingViewer(doc.id, false);
          console.log(error);
          toast.error(t('orderInfo.listFilesOrder.openFileError'));
        });
      return;
    }

    if (cachedFiles[doc.fileId]) {
      setImgUrl(cachedFiles[doc.fileId]);
      setCurrentDocId(doc.id);
      setIsOpen(true);
      return;
    }

    setLoadingViewer(doc.id, true);
    File.downloadFileById(doc.fileId)
      .then((response) => {
        const file = new Blob([response.data], {
          type: response.data.type || doc.contentType,
        });

        const fileURL = URL.createObjectURL(file);
        setImgUrl(fileURL);
        setCurrentDocId(doc.id);
        setIsOpen(true);
        setLoadingViewer(doc.id, false);

        setCachedFiles((prev) => ({
          ...prev,
          [doc.fileId]: fileURL,
        }));
      })
      .catch((error) => {
        setLoadingViewer(doc.id, false);
        console.log(error);
        toast.error(t('orderInfo.listFilesOrder.openFileError'));
      });
  };

  const onDownload = (doc) => {
    if (!doc) {
      toast.error(t('orderInfo.listFilesOrder.fileNotFound'));
      return;
    }
    setLoadingDownload(doc.id, true);

    if (cachedFiles[doc.fileId]) {
      const a = document.createElement("a");
      a.href = cachedFiles[doc.fileId];
      a.setAttribute("download", doc.fileName || "file");
      a.click();
      setLoadingDownload(doc.id, false);
      return;
    }

    File.downloadFileById(doc.fileId)
      .then((response) => {
        const file = new Blob([response.data], {
          type: response.data.type || doc.contentType,
        });

        const fileURL = URL.createObjectURL(file);
        const a = document.createElement("a");
        a.href = fileURL;
        a.setAttribute("download", doc.fileName || "file");
        a.click();

        setCachedFiles((prev) => ({
          ...prev,
          [doc.fileId]: fileURL,
        }));
      })
      .catch((error) => {
        console.log(error);
        toast.error(t('orderInfo.listFilesOrder.downloadFileError'));
      })
      .finally(() => {
        setLoadingDownload(doc.id, false);
      });
  };


  const handleConfirmDelete = () => {
    if (!docIdToDelete) return;
    setLoadingRemovinge(true);
  
    File.deleteDocumentOrders(docIdToDelete)
      .then(() => {
        toast.success(t('orderInfo.listFilesOrder.deleteSuccess'));
        setShowConfirmDelete(false);
        setDocIdToDelete(null);
        setLoadingRemovinge(false);
        refreshDocuments();
      })
      .catch((error) => {
        console.error(error);
        setLoadingRemovinge(false);
        setDocIdToDelete(null);
        toast.error(t('orderInfo.listFilesOrder.deleteError'));
      });
  };

  const displayedDocuments = showAllDocuments || documents.length <= 10
    ? documents 
    : documents.slice(0, 10);

  return (
    <>
      <div className={style.cargo_wrapper}>
        {documents && documents.length > 0 ? (
          <div className={style.documentsList}>
            {displayedDocuments.map((doc, index) => (
              <div key={index} className={style.documentItem}>
                <div className={style.documentItem_icon}>
                  <DocumentNewIcon />
                </div>
                <div className={style.documentItem_content}>
                  <div
                    className={cn(
                      style.documentItem_name,
                      isMobileView ? style.documentItem_name_mobile : style.documentItem_name_desktop,
                    )}
                  >
                    {doc.fileName || t('orderInfo.listFilesOrder.defaultFileName')}
                  </div>
                  <div className={style.documentItem_size}>
                    {getFileExtension(doc.fileName)} | {moment(doc?.createdAt).format("DD.MM.YYYY")} {t('orderInfo.listDocsOrder.dateTimeConnector')}{" "}
                    {moment(doc?.createdAt).format("HH:mm")}
                    {" "} от {doc.companyName}
                  </div>
                </div>

                {orderStatus !== "completed" && user && user?.companyId === doc?.companyId && (
                  <DeleteNewIcon
                    style={{ cursor: "pointer" }}
                    onClick={() => {
                      setDocIdToDelete(doc.fileId);
                      setShowConfirmDelete(true);
                    }}
                  />
                )}
                {loadingStatesDownload[doc?.id] ? (
                  <Loading inBlock marginBottom={0} />
                ) : (
                  <UnloadIcon
                    onClick={() => onDownload(doc)}
                    style={{ cursor: "pointer" }}
                  />
                )}

                {loadingStates[doc?.id] ? (
                  <Loading inBlock marginBottom={0} />
                ) : (
                  <EyeIcon
                    onClick={() => handleOpenFile(doc)}
                    style={{ cursor: "pointer" }}
                  />
                )}
              </div>
            ))}
            
            {documents.length > 10 && (
              <div 
                className={style.showMoreDocuments}
                onClick={() => setShowAllDocuments(!showAllDocuments)}
              >
                {showAllDocuments ? t('orderInfo.listFilesOrder.hide') : t('orderInfo.listFilesOrder.showMore', { count: documents.length - 10 })}
                <ArrowDownIcon 
                  style={{ 
                    transform: showAllDocuments ? 'rotate(180deg)' : 'rotate(0deg)',
                    marginLeft: '5px'
                  }} 
                />
              </div>
            )}
          </div>
        ) : null}
      </div>

      {isOpen && (
        <PhotoViewer
          imgUrl={imgUrl}
          onClose={handleClose}
          onDownload={() => {
            const doc = documents.find(x => x.id === currentDocId);
            onDownload(doc);
          }}
          ShowBtnDownload
        />
      )}

      {showConfirmDelete && (
        <div className={style.confirmModalOverlay}>
          <div className={style.confirmModal}>
            <p>{t('orderInfo.listFilesOrder.confirmDelete')}</p>
            <div className={style.confirmModal_actions}>
              <CustomButton
                isWhite
                text={t("common.cancel")}
                onClick={() => {
                  setShowConfirmDelete(false);
                  setDocIdToDelete(null);
                }}
              />
              <CustomButton
                text={t("common.confirm")}
                onClick={handleConfirmDelete}
                isLoading={loadingRemoving}
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ListFilesOrder;
